import React from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Science as ScienceIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import kaiveLogo from '@/assets/logo/kaive-logo-dark.png';

interface MenuBarProps {
  open: boolean;
  onClose: () => void;
  variant?: 'temporary' | 'permanent' | 'persistent';
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 72;

export const MenuBar: React.FC<MenuBarProps> = ({
  open,
  onClose,
  variant = 'temporary',
  collapsed = false,
  onToggleCollapse,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleNavigation = (e: React.MouseEvent, path: string) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return;
    }
    e.preventDefault();
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const navItemSx = {
    mx: 1,
    borderRadius: 1,
    textDecoration: 'none',
    color: 'inherit',
    '&.Mui-selected': {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': {
        backgroundColor: 'primary.dark',
      },
      '& .MuiListItemIcon-root': {
        color: 'primary.contrastText',
      },
    },
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 64,
        }}
      >
        {!collapsed && (
          <Box
            sx={{
              height: 32,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src={kaiveLogo}
              alt="Kaive Admin"
              sx={{
                height: 100,
                mt: '-34px',
                mb: '-34px',
              }}
            />
          </Box>
        )}

        {!isMobile && onToggleCollapse && (
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{ ml: collapsed ? 'auto' : 0 }}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>

      <Divider />

      <List sx={{ flex: 1, pt: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/companies"
            onClick={(e: React.MouseEvent) => handleNavigation(e, '/companies')}
            selected={isActive('/companies')}
            sx={navItemSx}
          >
            <ListItemIcon>
              <BusinessIcon />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Empresas" />}
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to="/trials"
            onClick={(e: React.MouseEvent) => handleNavigation(e, '/trials')}
            selected={isActive('/trials')}
            sx={navItemSx}
          >
            <ListItemIcon>
              <ScienceIcon />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Trials" />}
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : variant}
      open={isMobile ? open : true}
      onClose={onClose}
      sx={{
        width: collapsed && !isMobile ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed && !isMobile ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
        },
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
